"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useTranslations } from "next-intl"



interface RagSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentId: string;
  hasRagDocuments: boolean;
  currentSettings?: {
    chunk_limit?: number;
    vector_distance_limit?: number;
  };
  onSettingsUpdated?: (settings: { chunk_limit: number; vector_distance_limit: number }) => void;
}

// ElevenLabs defaults
const DEFAULT_CHUNK_LIMIT = 20;
const DEFAULT_VECTOR_DISTANCE = 0.6;

export function RagSettingsDialog({
  open,
  onOpenChange,
  agentId,
  hasRagDocuments,
  currentSettings,
  onSettingsUpdated,
}: RagSettingsDialogProps) {

  const tUi = useTranslations("Voice.AgentsVoiceSettingsRagSettingsDialog")
  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Local state for sliders
  const [chunkLimit, setChunkLimit] = useState(
    currentSettings?.chunk_limit ?? DEFAULT_CHUNK_LIMIT
  );
  const [vectorDistance, setVectorDistance] = useState(
    currentSettings?.vector_distance_limit ?? DEFAULT_VECTOR_DISTANCE
  );

  // Reset values when dialog opens
  useEffect(() => {
    if (open) {
      setChunkLimit(currentSettings?.chunk_limit ?? DEFAULT_CHUNK_LIMIT);
      setVectorDistance(currentSettings?.vector_distance_limit ?? DEFAULT_VECTOR_DISTANCE);
      setMessage(null);
    }
  }, [open, currentSettings]);

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);

    try {
      const response = await fetchWithAuth(
        `/api/agents/voice/${agentId}/rag-settings`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            chunk_limit: chunkLimit,
            vector_distance_limit: vectorDistance,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        // Update parent state with new values and close dialog
        onSettingsUpdated?.({ chunk_limit: chunkLimit, vector_distance_limit: vectorDistance });
        onOpenChange(false);
      } else {
        setMessage({
          type: "error",
          text: result.error || "Failed to update RAG settings",
        });
      }
    } catch (err) {
      console.error("Error saving RAG settings:", err);
      setMessage({
        type: "error",
        text: "Failed to update RAG settings",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    chunkLimit !== (currentSettings?.chunk_limit ?? DEFAULT_CHUNK_LIMIT) ||
    vectorDistance !== (currentSettings?.vector_distance_limit ?? DEFAULT_VECTOR_DISTANCE);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle><span key={tUi("rAGSettings")}>{tUi("rAGSettings")}</span></DialogTitle>
          <DialogDescription>
            <span key={tUi("configureRetrievalParametersForRAGKnowledgeBases")}>{tUi("configureRetrievalParametersForRAGKnowledgeBases")}</span></DialogDescription>
        </DialogHeader>

        {!hasRagDocuments ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <span key={tUi("noRAGKnowledgeBasesAreConnectedToThis")}>{tUi("noRAGKnowledgeBasesAreConnectedToThis")}</span></AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-6 py-4">
            {/* Chunk Limit */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="chunk-limit" className="text-sm font-medium">
                  <span key={tUi("chunkLimit")}>{tUi("chunkLimit")}</span></Label>
                <span className="text-sm font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  {chunkLimit}
                </span>
              </div>
              <Slider
                id="chunk-limit"
                min={1}
                max={20}
                step={1}
                value={[chunkLimit]}
                onValueChange={(value) => setChunkLimit(value[0])}
                disabled={isSaving}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                <span key={tUi("maximumNumberOfDocumentChunksToRetrieve1")}>{tUi("maximumNumberOfDocumentChunksToRetrieve1")}</span></p>
            </div>

            {/* Vector Distance Limit */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="vector-distance" className="text-sm font-medium">
                  <span key={tUi("vectorDistanceLimit")}>{tUi("vectorDistanceLimit")}</span></Label>
                <span className="text-sm font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  {vectorDistance.toFixed(2)}
                </span>
              </div>
              <Slider
                id="vector-distance"
                min={0.1}
                max={0.99}
                step={0.01}
                value={[vectorDistance]}
                onValueChange={(value) => setVectorDistance(value[0])}
                disabled={isSaving}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                <span key={tUi("maximumDistanceForVectorSimilaritySearch01")}>{tUi("maximumDistanceForVectorSimilaritySearch01")}</span></p>
            </div>

            {message?.type === "error" && (
              <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                <AlertDescription className="text-red-800 dark:text-red-200">
                  {message.text}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
          {hasRagDocuments && (
            <Button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span key={tCommonUi("saving")}>{tCommonUi("saving")}</span></>
              ) : (
                tUi("applyChanges")
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
